import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { describeCorrection, formatQuantity, planCountCorrection } from '@kronos/domain'
import { repository } from '../../data'
import type { ItemPage } from '../../data/inventory'
import { Modal } from '../kit'
import { CountStepper } from '../receive/CountStepper'

/**
 * "They said five; there are seven." The person types what is really on the
 * shelf and Kronos makes its records agree (`planCountCorrection`) — a
 * recorded recount, never an overwrite. The sentence above Save says exactly
 * what will happen; the server re-checks, because only it knows which units
 * have a history.
 */
export function CorrectCount({ item, onClose }: { item: ItemPage; onClose: () => void }): JSX.Element {
  const client = useQueryClient()
  const serialized = item.tracking === 'serialized'
  const [counted, setCounted] = useState(item.onShelf)
  const [note, setNote] = useState('')
  const [problem, setProblem] = useState('')

  // The preview for taking serialized units off cannot see which ones have a
  // history; it says the rule, and the server applies it.
  const preview =
    serialized && counted < item.onShelf
      ? `${item.onShelf - counted} will be taken off — the newest ones, never used.`
      : describeCorrection(planCountCorrection({ tracking: item.tracking, onShelf: item.onShelf, counted }), item.unit)

  const save = useMutation({
    mutationFn: () => repository.correctCount(item.id, counted, note),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['inventory-item', item.id] })
      void client.invalidateQueries({ queryKey: ['inventory'] })
      void client.invalidateQueries({ queryKey: ['inventory-summary'] })
      onClose()
    },
    onError: (e: Error) => setProblem(e.message.replace(/^\d{3}:\s*/, '')),
  })

  return (
    <Modal
      title="Correct the count"
      subtitle={item.name}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={save.isPending || counted === item.onShelf} onClick={() => save.mutate()}>
            Save count
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <p className="muted" style={{ margin: 0 }}>
          Kronos has <strong>{formatQuantity(item.onShelf, serialized ? '' : item.unit)}</strong> on the shelf. How many are really
          there?
        </p>
        <CountStepper value={counted} onChange={setCounted} whole={serialized} min={0} />
        <p className={counted === item.onShelf ? 'muted' : ''} style={{ margin: 0 }}>
          {preview}
        </p>
        <label className="field">
          <span className="field-label">Why (optional)</span>
          <input value={note} maxLength={500} placeholder="e.g. Box had 7, not 5" onChange={(e) => setNote(e.target.value)} />
        </label>
        {problem ? (
          <p className="critical" role="alert" style={{ margin: 0 }}>
            {problem}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
